<!-- ═══════════════════════════════════════════════════════
     iRTA — Internal RTA
     PDM-focused: Revise/Create documents & parts in FileVault
     ═══════════════════════════════════════════════════════ -->

<!-- ═══ CREATE MODE ═══ -->
@if(isCreateMode) {
<div class="irta-page">
    <div class="irta-create-container">
        <kendo-card class="irta-create-card" width="100%">
            <kendo-card-body>
                <!-- Header -->
                <div class="irta-create-header">
                    <div class="irta-create-icon">
                        <span class="k-icon k-font-icon k-i-file-wrench"></span>
                    </div>
                    <div>
                        <h2 class="irta-create-title">New Internal RTA</h2>
                        <p class="irta-create-subtitle">
                            Create an iRTA to revise or create documents and parts in FileVault.
                        </p>
                    </div>
                </div>

                <!-- FileVault Change Create -->
                <div class="irta-create-form">
                    <fv-change-create (saved)="onChangeSaved($event)" (cancelled)="onChangeCancelled()">
                    </fv-change-create>
                </div>
            </kendo-card-body>
        </kendo-card>
    </div>
</div>
}

<!-- ═══ EDIT MODE ═══ -->
@if(!isCreateMode) {
<div class="irta-page">
    <!-- Loading -->
    @if(isLoading) {
    <div class="irta-loading">
        <kendo-loader type="infinite-spinner" size="large"></kendo-loader>
        <p>Loading iRTA...</p>
    </div>
    }

    <div>
        <!-- ═══ Tabs ═══ -->
        <div class="container irta-content">
            <kendo-tabstrip (tabSelect)="onTabSelect($event)">
                <!-- ── Details ── -->
                <kendo-tabstrip-tab title="Details" [selected]="true">
                    <ng-template kendoTabContent>
                        <div class="irta-tab-content">
                            <div class="irta-section">
                                <fv-change-detail [changeId]="changeId" [currentLoginId]="currentUserLoginId" (stateChanged)="onChangeStateChanged($event)"></fv-change-detail>
                            </div>
                        </div>
                    </ng-template>
                </kendo-tabstrip-tab>
                <!-- ── Documents & Files ── -->
                <kendo-tabstrip-tab title="Documents & Files" >
                    <ng-template kendoTabContent>
                        <div class="irta-tab-content">
                            <div class="irta-section">
                                <h3 class="section-title">
                                    <span class="k-icon k-font-icon k-i-upload"></span>
                                    Upload Documents
                                </h3>
                                <fv-file-upload [targetId]="id || 0" [applicationTypeId]="2" [autoCreateDocument]="true"
                                    (uploadCompleted)="onUploadCompleted($event)"></fv-file-upload>
                            </div>
                            <div class="irta-section">
                                <h3 class="section-title">
                                    <span class="k-icon k-font-icon k-i-folder-open"></span>
                                    Associated Files
                                </h3>
                                <fv-file-list [initialCriteria]="{ targetId: id, includeSubdirectories: true }"
                                    [targetId]="id || 0" [applicationTypeId]="2"
                                    [visibleApplicationTypeNames]="['Rta', 'Ejdf']" [showToolbar]="true"
                                    [showCheckout]="true" [refreshToken]="fileListRefreshToken"></fv-file-list>
                            </div>
                        </div>
                    </ng-template>
                </kendo-tabstrip-tab>

                <!-- ── Part Lifecycle ── -->
                <kendo-tabstrip-tab title="Part Lifecycle">
                    <ng-template kendoTabContent>
                        <div class="irta-tab-content">
                            @if(partId) {
                            <div class="irta-lifecycle-grid">
                                <div class="irta-card">
                                    <div class="irta-card-header">
                                        <span class="k-icon k-font-icon k-i-track-changes"></span>
                                        <h3>Current Lifecycle State</h3>
                                    </div>
                                    <div class="irta-card-body">
                                        <fv-lifecycle-badge [entityType]="'part'" [entityId]="partId" [showIcon]="true"
                                            [showIndicators]="true" [size]="'large'"></fv-lifecycle-badge>
                                    </div>
                                </div>
                                <div class="irta-card">
                                    <div class="irta-card-header">
                                        <span class="k-icon k-font-icon k-i-gear"></span>
                                        <h3>Lifecycle Actions</h3>
                                    </div>
                                    <div class="irta-card-body">
                                        <fv-lifecycle-actions [entityType]="'part'" [entityId]="partId"
                                            [showCurrentState]="true"
                                            (transitionCompleted)="onLifecycleTransition($event)"></fv-lifecycle-actions>
                                    </div>
                                </div>
                                <div class="irta-card irta-card-full">
                                    <div class="irta-card-header">
                                        <span class="k-icon k-font-icon k-i-clock"></span>
                                        <h3>Lifecycle History</h3>
                                    </div>
                                    <div class="irta-card-body">
                                        <fv-lifecycle-history [entityType]="'part'" [entityId]="partId"
                                            [viewMode]="'timeline'" [showViewToggle]="true"></fv-lifecycle-history>
                                    </div>
                                </div>
                            </div>
                            } @else {
                            <div class="irta-empty-state">
                                <span class="k-icon k-font-icon k-i-info"></span>
                                @if(isLoadingPart) {
                                <p>Loading FileVault part data...</p>
                                <kendo-loader size="medium"></kendo-loader>
                                } @else {
                                <p>No FileVault part linked to this request yet.</p>
                                <p class="irta-empty-hint">A material number is required to manage the part lifecycle.
                                </p>
                                }
                            </div>
                            }
                        </div>
                    </ng-template>
                </kendo-tabstrip-tab>

                <!-- ── BOM ── -->
                <kendo-tabstrip-tab title="BOM">
                    <ng-template kendoTabContent>
                        <div class="irta-tab-content">
                            @if(partId && partNumber) {
                            <div class="irta-section">
                                <h3 class="section-title">
                                    <span class="k-icon k-font-icon k-i-list-unordered"></span>
                                    Bill of Materials
                                </h3>
                                <fv-bom-editor [partNumber]="partNumber" [canEdit]="true"
                                    (bomChanged)="onBomChanged($event)"></fv-bom-editor>
                            </div>
                            <div class="irta-section" style="margin-top: 24px;">
                                <h3 class="section-title">
                                    <span class="k-icon k-font-icon k-i-hyperlink-open"></span>
                                    Where Used
                                </h3>
                                <fv-where-used [partNumber]="partNumber" [viewMode]="'tree'" [showToolbar]="true"
                                    [maxDepth]="3"></fv-where-used>
                            </div>
                            } @else {
                            <div class="irta-empty-state">
                                <span class="k-icon k-font-icon k-i-info"></span>
                                <p>No part linked. BOM management requires a FileVault part number.</p>
                            </div>
                            }
                        </div>
                    </ng-template>
                </kendo-tabstrip-tab>

                <!-- ── Document Links ── -->
                <kendo-tabstrip-tab title="Document Links">
                    <ng-template kendoTabContent>
                        <div class="irta-tab-content">
                            @if(partId) {
                            <div class="irta-section">
                                <h3 class="section-title">
                                    <span class="k-icon k-font-icon k-i-link-horizontal"></span>
                                    Linked Documents
                                </h3>
                                <fv-document-links [partId]="partId" [canEdit]="true"></fv-document-links>
                            </div>
                            <div class="irta-section" style="margin-top: 24px;">
                                <h3 class="section-title">
                                    <span class="k-icon k-font-icon k-i-file-add"></span>
                                    Drawing Numbers
                                </h3>
                                <fv-drawing-number-list></fv-drawing-number-list>
                            </div>
                            } @else {
                            <div class="irta-empty-state">
                                <span class="k-icon k-font-icon k-i-info"></span>
                                <p>No part linked. Document linking requires a FileVault part.</p>
                            </div>
                            }
                        </div>
                    </ng-template>
                </kendo-tabstrip-tab>

                <!-- ── Change Management ── -->
                <kendo-tabstrip-tab title="Changes">
                    <ng-template kendoTabContent>
                        <div class="irta-tab-content">
                            <div class="irta-section">
                                <h3 class="section-title">
                                    <span class="k-icon k-font-icon k-i-track-changes-accept"></span>
                                    Change Orders
                                </h3>
                                <fv-change-list [showCreateButton]="true" [showActions]="true"
                                    (createClick)="openChangeDialog()"></fv-change-list>
                            </div>
                        </div>
                    </ng-template>
                </kendo-tabstrip-tab>

            </kendo-tabstrip>

            <!-- Chat Window -->
            @if (viewChat) {
            <kendo-window [resizable]="false" [(left)]="windowLeft" [minWidth]="300" [minHeight]="500"
                title="iRTA Communications" [keepContent]="true" autoFocusedElement="#messageBoxInput"
                (close)="viewChat=false">
                <app-communication-data [_initialcommunications]="request.communication"
                    [draftId]="id"></app-communication-data>
            </kendo-window>}
        </div>
    </div>

    <!-- ═══ Drawing Number Dialog ═══ -->
    <kendo-dialog *ngIf="showDrawingDialog" title="Request Drawing Number" [minWidth]="400" [width]="500"
        (close)="closeDrawingDialog()">
        <div class="irta-dialog-content">
            <p class="dialog-description">
                Request a new drawing number for iRTA #{{ id }}.
            </p>
            <fv-drawing-number-request [rtaNum]="id" [createdBy]="currentUserId" [subPsl]="(request$ | async)?.subPsl"
                [productLine]="'Matrix'" [partNumber]="materialNumber">
            </fv-drawing-number-request>
        </div>
        <kendo-dialog-actions>
            <button kendoButton look="outline" (click)="closeDrawingDialog()">Close</button>
        </kendo-dialog-actions>
    </kendo-dialog>

    <!-- ═══ Change Order Dialog ═══ -->
    <kendo-dialog *ngIf="showChangeDialog" title="Create Change Order" [minWidth]="400" [width]="600"
        (close)="closeChangeDialog()">
        <div class="irta-dialog-content">
            <p class="dialog-description">
                Create a change order to initiate a revision workflow in FileVault for documents and parts associated
                with
                iRTA #{{ id }}.
            </p>
            <kendo-formfield>
                <kendo-label text="Reason for Change *"></kendo-label>
                <kendo-dropdownlist [(ngModel)]="changeReason"
                    [data]="['Design Revision', 'Material Change', 'Customer Requirement', 'Manufacturing Update', 'Quality Correction', 'Cost Optimization', 'Other']"
                    [defaultItem]="'-- Select Reason --'" [valuePrimitive]="true">
                </kendo-dropdownlist>
            </kendo-formfield>
            <kendo-formfield style="margin-top: 16px;">
                <kendo-label text="Description"></kendo-label>
                <kendo-textarea [(ngModel)]="changeDescription" [rows]="4"
                    placeholder="Describe the change in detail..."></kendo-textarea>
            </kendo-formfield>
        </div>
        <kendo-dialog-actions>
            <button kendoButton look="outline" (click)="closeChangeDialog()"
                [disabled]="isSubmittingChange">Cancel</button>
            <button kendoButton themeColor="primary" (click)="submitChangeOrder()"
                [disabled]="isSubmittingChange || !changeReason?.trim()">
                @if(isSubmittingChange) {
                <kendo-loader size="small" themeColor="light"></kendo-loader>
                <span style="margin-left: 8px;">Creating...</span>
                } @else {
                Create Change Order
                }
            </button>
        </kendo-dialog-actions>
    </kendo-dialog>

    <!-- ═══ Part Number Request Dialog ═══ -->
    <kendo-dialog *ngIf="showPartNumberDialog" title="Request New Part Numbers" [minWidth]="600" [width]="950"
        (close)="closePartNumberDialog()">
        <div class="irta-dialog-content part-number-dialog">
            <p class="dialog-description">
                Add part number requests manually or import from a CSV file.
            </p>

            <!-- CSV Drop Zone -->
            <div class="csv-dropzone" [class.csv-dropzone-active]="csvDragOver"
                (dragover)="onCsvDragOver($event)"
                (dragleave)="onCsvDragLeave($event)"
                (drop)="onCsvDrop($event)">
                <span class="k-icon k-font-icon k-i-upload"></span>
                <p>Drag &amp; drop a CSV file here, or
                    <label class="csv-browse-link">
                        browse
                        <input type="file" accept=".csv" (change)="onCsvFileSelected($event)" hidden />
                    </label>
                </p>
                <small>Format: MaterialWeight, ComponentDescription, MRP, OldMaterial, PurchasePart, ProcurementType,
                    RequestTargetId, MaterialType, UnitofMeasure, ProductHierarchy</small>
            </div>

            <!-- CSV Parse Warnings -->
            <div class="csv-warnings" *ngIf="csvParseErrors.length > 0">
                <strong>Import warnings:</strong>
                <ul>
                    <li *ngFor="let err of csvParseErrors">{{ err }}</li>
                </ul>
            </div>

            <!-- Part Number Grid -->
            <div class="part-grid-wrapper" *ngIf="partNumberRequests.length > 0">
                <div class="part-grid-header">
                    <span>{{ partNumberRequests.length }} row(s)</span>
                    <button kendoButton look="flat" themeColor="primary" (click)="addEmptyPartRow()">+ Add Row</button>
                </div>

                <div class="part-grid-scroll">
                    <table class="part-grid-table">
                        <thead>
                            <tr>
                                <th style="width:80px">Weight</th>
                                <th style="width:220px">Description <small>(≤40 chars)</small></th>
                                <th style="width:60px">MRP</th>
                                <th style="width:110px">Old Material</th>
                                <th style="width:110px">Purchase Part</th>
                                <th style="width:90px">Procurement</th>
                                <th style="width:90px">Target ID</th>
                                <th style="width:90px">Mat. Type</th>
                                <th style="width:60px">UoM</th>
                                <th style="width:120px">Prod Hierarchy</th>
                                <th style="width:40px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let req of partNumberRequests; let i = index">
                                <td><input kendoTextBox [(ngModel)]="req.materialWeight" class="grid-input" /></td>
                                <td>
                                    <input kendoTextBox [(ngModel)]="req.componentDescription" class="grid-input"
                                        [maxlength]="40"
                                        [class.input-error]="req.componentDescription && req.componentDescription.length > 40" />
                                    <small class="char-count"
                                        *ngIf="req.componentDescription">{{ req.componentDescription.length }}/40</small>
                                </td>
                                <td><input kendoTextBox [(ngModel)]="req.mrp" class="grid-input" /></td>
                                <td><input kendoTextBox [(ngModel)]="req.oldMaterial" class="grid-input" /></td>
                                <td>
                                    <kendo-dropdownlist [(ngModel)]="req.purchasePart"
                                        [data]="purchasePartOptions"
                                        textField="text" valueField="value"
                                        [valuePrimitive]="true"
                                        class="grid-dropdown">
                                    </kendo-dropdownlist>
                                </td>
                                <td>
                                    <kendo-dropdownlist [(ngModel)]="req.procurementType"
                                        [data]="procurementTypeOptions"
                                        textField="text" valueField="value"
                                        [valuePrimitive]="true"
                                        class="grid-dropdown">
                                    </kendo-dropdownlist>
                                </td>
                                <td><input kendoTextBox [(ngModel)]="req.requestTargetId" class="grid-input" type="number" /></td>
                                <td>
                                    <kendo-dropdownlist [(ngModel)]="req.materialType"
                                        [data]="materialTypeOptions"
                                        textField="text" valueField="value"
                                        [valuePrimitive]="true"
                                        class="grid-dropdown">
                                    </kendo-dropdownlist>
                                </td>
                                <td><input kendoTextBox [(ngModel)]="req.unitofMeasure" class="grid-input" /></td>
                                <td>
                                    <input kendoTextBox [(ngModel)]="req.productHierarchy" class="grid-input"
                                        [class.input-error]="req.productHierarchy && !req.productHierarchy.startsWith('18')" />
                                </td>
                                <td>
                                    <button kendoButton look="flat" themeColor="error"
                                        (click)="removePartRow(i)" title="Remove row"
                                        [disabled]="isSubmittingPartNumbers">✕</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div *ngIf="partNumberRequests.length === 0" class="part-empty-state">
                <p>No rows yet. Add a row manually or import a CSV file.</p>
            </div>
        </div>

        <kendo-dialog-actions>
            <button kendoButton look="outline" (click)="closePartNumberDialog()"
                [disabled]="isSubmittingPartNumbers">Cancel</button>
            <button kendoButton themeColor="primary" (click)="submitPartNumberRequest()"
                [disabled]="isSubmittingPartNumbers || partNumberRequests.length === 0">
                @if(isSubmittingPartNumbers) {
                <kendo-loader size="small" themeColor="light"></kendo-loader>
                <span style="margin-left: 8px;">Submitting...</span>
                } @else {
                Submit {{ partNumberRequests.length }} Request(s)
                }
            </button>
        </kendo-dialog-actions>
    </kendo-dialog>

    <!-- ═══ FAB ═══ -->
    @if(!isCreateMode && !isLoading) {
    <kendo-floatingactionbutton themeColor="tertiary" [svgIcon]="gearsIcon" [positionMode]="'fixed'"
        [align]="{ horizontal: 'end', vertical: 'bottom' }" style="right: 16px; bottom: 16px;"
        (dialItemClick)="onDialItemClick($event)" [dialItems]="actionItems">
    </kendo-floatingactionbutton>
    }
</div>
}